import { ChangeDetectionStrategy, ChangeDetectorRef, Component, NgZone, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ApiService, AuthService } from '@homeleite/arch';
import { jwtDecode } from 'jwt-decode';
import { lastValueFrom } from 'rxjs';
import { finalize, timeout } from 'rxjs/operators';
import { currentApiEnvKey, currentApiEnvironment } from '../../core/api-environments';
import { associationView, setAssociationView } from '../../core/association-view';

interface SrgSclSettingsView {
    baseUrl: string | null;
    tokenConfigured: boolean;
    tokenHint: string | null;
    enabled: boolean;
    updatedBy: string | null;
    updatedAt: string | null;
    allowedHosts: string[];
    encryptionAvailable: boolean;
}

interface AssociationRow {
    id: string;
    name: string;
    code: number | null;
    tokenConfigured: boolean;
    tokenHint: string | null;
    enabled: boolean;
    baseUrl: string | null;
    statusKnown: boolean;
}

type ViewState = 'loading' | 'error' | 'unavailable' | 'ready';

@Component({
    selector: 'homeleite-integration-settings',
    templateUrl: './integration-settings.component.html',
    styleUrls: ['./integration-settings.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class IntegrationSettingsComponent implements OnInit, OnDestroy {
    state: ViewState = 'loading';
    saving = false;
    adding = false;
    creating = false;
    showAll = false;
    associationId = '';
    overview: AssociationRow[] = [];
    settings: SrgSclSettingsView | null = null;
    private readonly onView = () => {
        this.zone.run(() => {
            this.showAll = associationView() === 'all';
            this.refresh();
        });
    };
    readonly envLabel = currentApiEnvironment().label;
    readonly isProduction = currentApiEnvKey() === 'production';

    readonly form = this.fb.group({
        baseUrl: ['', [Validators.required, Validators.pattern(/^https:\/\/.+/)]],
        token: [''],
        enabled: [false]
    });

    readonly edit = this.fb.group({
        name: ['', [Validators.required, Validators.minLength(2)]],
        baseUrl: ['', [Validators.required, Validators.pattern(/^https:\/\/.+/)]],
        token: [''],
        enabled: [false]
    });
    editingId: string | null = null;
    editSaving = false;
    editTesting = false;
    editTestResult: { ok: boolean; message: string; elapsedMs?: number } | null = null;

    readonly draft = this.fb.group({
        name: ['', [Validators.required, Validators.minLength(2)]],
        id: ['', [Validators.required, Validators.pattern(/^[A-Za-z][A-Za-z0-9]{1,15}$/)]],
        code: ['', [Validators.required, Validators.pattern(/^[1-9]\d*$/)]],
        baseUrl: ['', [Validators.required, Validators.pattern(/^https:\/\/.+/)]],
        token: ['', Validators.required]
    });

    constructor(
        private fb: FormBuilder,
        private api: ApiService,
        private auth: AuthService,
        private snackBar: MatSnackBar,
        private cd: ChangeDetectorRef,
        private zone: NgZone
    ) {}

    ngOnInit(): void {
        this.associationId = this.readAssociationId();
        if (!associationView()) {
            setAssociationView('all');
        }
        this.showAll = associationView() === 'all';
        window.addEventListener('hl-association-view', this.onView);
        this.refresh();
    }

    ngOnDestroy(): void {
        window.removeEventListener('hl-association-view', this.onView);
    }

    get associationName(): string {
        const row = this.overview.find((item) => item.id === this.associationId);
        return row?.name && row.name !== this.associationId ? row.name : '';
    }

    showDirectory(): void {
        this.creating = false;
        setAssociationView('all');
    }

    refresh(): void {
        if (this.showAll) {
            this.loadOverview();
            return;
        }
        this.load();
    }

    get tokenRequired(): boolean {
        return !this.settings?.tokenConfigured;
    }

    loadOverview(): void {
        this.state = 'loading';
        this.cd.markForCheck();
        this.api.root
            .path('integration-settings', 'srg-scl', 'overview')
            .getJson<AssociationRow[]>()
            .pipe(timeout(15000))
            .subscribe({
                next: (rows) => {
                    if (!Array.isArray(rows)) {
                        this.loadOverviewFromTenants();
                        return;
                    }
                    this.overview = rows.map((row) => ({
                        ...row,
                        statusKnown: true
                    }));
                    this.prefillDraftUrl();
                    this.state = 'ready';
                    this.cd.markForCheck();
                },
                error: () => this.loadOverviewFromTenants()
            });
    }

    load(): void {
        this.state = 'loading';
        this.cd.markForCheck();
        this.api.root
            .path('integration-settings', 'srg-scl')
            .getJson<SrgSclSettingsView>()
            .pipe(timeout(15000))
            .subscribe({
                next: (s) => {
                    this.settings = s;
                    this.form.patchValue({ baseUrl: s.baseUrl ?? '', token: '', enabled: s.enabled });
                    if (!this.draft.value.baseUrl) {
                        this.draft.patchValue({ baseUrl: s.baseUrl ?? '' });
                    }
                    if (!this.overview.length) {
                        this.loadOverviewQuiet();
                    }
                    this.state = 'ready';
                    this.cd.markForCheck();
                },
                error: (e) => {
                    this.state = e?.status === 404 ? 'unavailable' : 'error';
                    this.cd.markForCheck();
                }
            });
    }

    testing = false;
    testResult: { ok: boolean; message: string; elapsedMs?: number } | null = null;

    test(): void {
        const { baseUrl, token } = this.form.value;
        if (this.form.controls.baseUrl.invalid || (this.tokenRequired && !token)) {
            this.form.markAllAsTouched();
            this.snackBar.open('Informe URL https e o token para testar', 'OK', { duration: 4000 });
            return;
        }
        this.testing = true;
        this.testResult = null;
        this.cd.markForCheck();
        this.api.root
            .path('integration-settings', 'srg-scl', 'test')
            .postJson({ baseUrl: (baseUrl ?? '').trim(), token: token || null, enabled: true })
            .pipe(
                timeout(20000),
                finalize(() => {
                    this.testing = false;
                    this.cd.markForCheck();
                })
            )
            .subscribe({
                next: (r: any) => {
                    this.testResult = r;
                    this.cd.markForCheck();
                },
                error: (e) => {
                    this.testResult = {
                        ok: false,
                        message:
                            e?.status === 400
                                ? 'URL inválida ou host não permitido'
                                : 'Não foi possível executar o teste (API indisponível ou tempo esgotado)'
                    };
                    this.cd.markForCheck();
                }
            });
    }

    save(): void {
        if (this.form.invalid || (this.tokenRequired && !this.form.value.token)) {
            this.form.markAllAsTouched();
            this.snackBar.open('Informe URL https e, na primeira vez, o token', 'OK', { duration: 4000 });
            return;
        }
        if (this.isProduction && !confirm('Você está alterando a integração SRG-SCL de PRODUÇÃO. Confirma?')) {
            return;
        }
        this.saving = true;
        this.cd.markForCheck();
        const { baseUrl, token, enabled } = this.form.value;
        this.api.root
            .path('integration-settings', 'srg-scl')
            .putJson({ baseUrl: (baseUrl ?? '').trim(), token: token || null, enabled: !!enabled })
            .pipe(
                timeout(15000),
                finalize(() => {
                    this.saving = false;
                    this.cd.markForCheck();
                })
            )
            .subscribe({
                next: () => {
                    this.snackBar.open('Configuração salva', 'OK', { duration: 4000 });
                    this.load();
                },
                error: (e) =>
                    this.snackBar.open(this.saveError(e), 'OK', { duration: 6000 })
            });
    }

    private saveError(e: { status?: number; error?: unknown }): string {
        if (e?.status === 503) {
            return 'Servidor sem chave de criptografia configurada';
        }
        if (e?.status === 403) {
            return 'Sem permissão de administrador global para gravar o token desta associação';
        }
        return this.serverMessage(e) || 'Não foi possível salvar (verifique URL e permissões)';
    }

    private serverMessage(e: { error?: unknown }): string {
        const body = e?.error;
        if (typeof body === 'string') {
            return body;
        }
        if (body && typeof body === 'object') {
            const record = body as { message?: unknown; details?: unknown };
            return String(record.message ?? record.details ?? '');
        }
        return '';
    }

    async addAssociation(): Promise<void> {
        if (this.isProduction || this.draft.invalid) {
            this.draft.markAllAsTouched();
            this.snackBar.open('Informe nome, sigla, código SRG-SCL, URL https e o token', 'OK', {
                duration: 4000
            });
            return;
        }
        const name = (this.draft.value.name ?? '').trim();
        const id = (this.draft.value.id ?? '').trim().toUpperCase();
        const code = Number(this.draft.value.code);
        const baseUrl = (this.draft.value.baseUrl ?? '').trim();
        const token = this.draft.value.token ?? '';
        if (id === 'APCBRH' || code === 13) {
            this.snackBar.open('APCBRH e o código 13 já existem', 'OK', { duration: 4000 });
            return;
        }
        if (
            !confirm(
                `Adicionar ${id} — ${name}? Ela entra no seletor e o painel passa a mostrar só os clientes dela.`
            )
        ) {
            return;
        }
        this.adding = true;
        this.cd.markForCheck();
        let created = false;
        try {
            await lastValueFrom(
                this.api.root.path('/associations').postJson({ id, code, name }).pipe(timeout(15000))
            );
            created = true;
            const session = await lastValueFrom(
                this.api.root
                    .path('/association-token')
                    .postJson<{ token: string }>({ tenantId: id })
                    .pipe(timeout(15000))
            );
            if (!session?.token) {
                throw new Error('sem token');
            }
            this.auth.publishLogin(session.token);
            setAssociationView('one');
            await lastValueFrom(
                this.api.root
                    .path('integration-settings', 'srg-scl')
                    .putJson({ baseUrl, token, enabled: true })
                    .pipe(timeout(15000))
            );
            location.reload();
        } catch (error) {
            this.adding = false;
            this.cd.markForCheck();
            this.snackBar.open(
                created
                    ? 'Associação criada. Se o token não gravou, selecione ela na faixa e salve de novo nesta tela.'
                    : this.apiMessage(error),
                'OK',
                { duration: 6000 }
            );
            if (created) {
                location.reload();
            }
        }
    }

    async enterAssociation(row: AssociationRow): Promise<void> {
        if (row.id === this.associationId) {
            setAssociationView('one');
            return;
        }
        const label = row.name && row.name !== row.id ? `${row.id} — ${row.name}` : row.id;
        if (!confirm(`Entrar na associação ${label}? O painel passa a mostrar só os clientes dela.`)) {
            return;
        }
        try {
            const session = await lastValueFrom(
                this.api.root
                    .path('/association-token')
                    .postJson<{ token: string }>({ tenantId: row.id })
                    .pipe(timeout(15000))
            );
            if (!session?.token) {
                throw new Error('sem token');
            }
            setAssociationView('one');
            this.auth.publishLogin(session.token);
            location.reload();
        } catch {
            this.snackBar.open('Não foi possível entrar nessa associação.', 'OK', { duration: 4000 });
        }
    }

    startEdit(row: AssociationRow): void {
        if (this.isProduction) {
            return;
        }
        this.editingId = row.id;
        this.editTestResult = null;
        this.edit.reset({ name: row.name, baseUrl: row.baseUrl ?? '', token: '', enabled: row.enabled });
        if (row.id === 'APCBRH') {
            this.edit.controls.name.disable();
        }
        this.cd.markForCheck();
    }

    cancelEdit(): void {
        this.editingId = null;
        this.editTestResult = null;
        this.cd.markForCheck();
    }

    testEdit(row: AssociationRow): void {
        const { baseUrl, token } = this.edit.getRawValue();
        if (this.edit.controls.baseUrl.invalid || (!row.tokenConfigured && !token)) {
            this.edit.controls.baseUrl.markAsTouched();
            this.snackBar.open('Informe URL https e o token para testar', 'OK', { duration: 4000 });
            return;
        }
        this.editTesting = true;
        this.editTestResult = null;
        this.cd.markForCheck();
        this.api.root
            .path('integration-settings', 'srg-scl', 'test')
            .postJson({ tenantId: row.id, baseUrl: (baseUrl ?? '').trim(), token: token || null, enabled: true })
            .pipe(
                timeout(20000),
                finalize(() => {
                    this.editTesting = false;
                    this.cd.markForCheck();
                })
            )
            .subscribe({
                next: (r: any) => {
                    this.editTestResult = r;
                    this.cd.markForCheck();
                },
                error: (e) => {
                    this.editTestResult = {
                        ok: false,
                        message:
                            e?.status === 400
                                ? this.serverMessage(e) || 'URL inválida ou host não permitido'
                                : 'Não foi possível executar o teste (API indisponível ou tempo esgotado)'
                    };
                    this.cd.markForCheck();
                }
            });
    }

    async saveEdit(row: AssociationRow): Promise<void> {
        if (this.edit.invalid) {
            this.edit.markAllAsTouched();
            return;
        }
        const { name, baseUrl, token, enabled } = this.edit.getRawValue();
        if (!row.tokenConfigured && !token) {
            this.snackBar.open('Informe o token desta associação', 'OK', { duration: 4000 });
            return;
        }
        const newName = (name ?? '').trim();
        this.editSaving = true;
        this.cd.markForCheck();
        try {
            if (row.id !== 'APCBRH' && newName !== row.name.trim()) {
                await lastValueFrom(
                    this.api.root
                        .path('/associations', 'rename')
                        .putJson({ id: row.id, name: newName })
                        .pipe(timeout(15000))
                );
            }
            await lastValueFrom(
                this.api.root
                    .path('integration-settings', 'srg-scl')
                    .putJson({
                        tenantId: row.id,
                        baseUrl: (baseUrl ?? '').trim(),
                        token: token || null,
                        enabled: !!enabled
                    })
                    .pipe(timeout(15000))
            );
            this.snackBar.open(`${row.id} salva`, 'OK', { duration: 4000 });
            this.editingId = null;
            this.loadOverview();
        } catch (e) {
            this.snackBar.open(this.saveError(e as { status?: number; error?: unknown }), 'OK', { duration: 6000 });
        } finally {
            this.editSaving = false;
            this.cd.markForCheck();
        }
    }

    private loadOverviewQuiet(): void {
        this.api.root
            .path('integration-settings', 'srg-scl', 'overview')
            .getJson<AssociationRow[]>()
            .pipe(timeout(15000))
            .subscribe({
                next: (rows) => {
                    if (!Array.isArray(rows)) {
                        return;
                    }
                    this.overview = rows.map((row) => ({ ...row, statusKnown: true }));
                    this.cd.markForCheck();
                }
            });
    }

    private loadOverviewFromTenants(): void {
        this.api.root
            .path('/public/tenants')
            .getJson<Array<{ id: string; name: string }>>()
            .pipe(timeout(15000))
            .subscribe({
                next: (rows) => {
                    this.overview = (Array.isArray(rows) ? rows : []).map((row) => ({
                        id: row.id,
                        name: row.name,
                        code: null,
                        tokenConfigured: false,
                        tokenHint: null,
                        enabled: false,
                        baseUrl: null,
                        statusKnown: false
                    }));
                    this.state = 'ready';
                    this.cd.markForCheck();
                },
                error: () => {
                    this.state = 'error';
                    this.cd.markForCheck();
                }
            });
    }

    private prefillDraftUrl(): void {
        if (this.draft.value.baseUrl) {
            return;
        }
        const known = this.overview.find((row) => row.baseUrl)?.baseUrl;
        if (known) {
            this.draft.patchValue({ baseUrl: known });
        }
    }

    private readAssociationId(): string {
        try {
            return jwtDecode<{ tenantId?: string }>(this.auth.currentToken).tenantId || 'APCBRH';
        } catch {
            return 'APCBRH';
        }
    }

    private apiMessage(error: unknown): string {
        const body = (error as { error?: unknown })?.error;
        if (typeof body === 'string' && body.trim()) {
            return body;
        }
        if (body && typeof body === 'object') {
            const record = body as { message?: string; details?: string };
            if (record.message) {
                return record.message;
            }
            if (record.details) {
                return record.details;
            }
        }
        return 'Não foi possível adicionar a associação. Confira sigla, código e token.';
    }
}
